import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import MainLayout from '@/components/layouts/MainLayout';
import PageHeader from '@/components/common/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Eye } from 'lucide-react';
import { formatDate, formatCurrency } from '@/utils/formatters';
import { useAuth } from '@/contexts/AuthContext';
import { useRoyaltyReports } from '@/hooks/useRoyaltyReports';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';

interface QuarterlyReport {
  quarter: number;
  year: number;
  totalUnitsSold: number;
  totalRoyaltyAmount: number;
  bookCount: number;
  status: string;
  reports: any[];
}

const RoyaltyReports: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data: royaltyReports, isLoading } = useRoyaltyReports(user?.id);
  
  const [yearFilter, setYearFilter] = useState<string>('all');
  
  // Extract unique years for filter
  const years = React.useMemo(() => {
    if (!royaltyReports) return [];
    
    const uniqueYears = new Set(royaltyReports.map(report => report.year));
    return Array.from(uniqueYears).sort((a, b) => b - a); // Sort descending
  }, [royaltyReports]);
  
  // Group reports by quarter and combine all books, counting unique book titles
  const quarterlyReports = React.useMemo(() => {
    if (!royaltyReports) return [];
    
    const grouped = royaltyReports.reduce((acc, report) => {
      const key = `${report.year}-Q${report.quarter}`;
      
      if (!acc[key]) {
        acc[key] = {
          quarter: report.quarter,
          year: report.year,
          totalUnitsSold: 0,
          totalRoyaltyAmount: 0,
          bookCount: 0,
          status: report.status,
          reports: [],
          uniqueBookTitles: new Set()
        };
      }
      
      acc[key].totalUnitsSold += report.unitsSold || report.salesCount || 0;
      acc[key].totalRoyaltyAmount += report.royaltyAmount || report.amount || 0;
      acc[key].reports.push(report);
      
      // Count unique book titles only
      if (report.books?.title) {
        acc[key].uniqueBookTitles.add(report.books.title);
      }
      
      return acc;
    }, {} as Record<string, QuarterlyReport & { uniqueBookTitles: Set<string> }>);
    
    // Convert Set size to bookCount and remove the Set from final object
    return Object.values(grouped).map(({ uniqueBookTitles, ...rest }) => ({
      ...rest,
      bookCount: uniqueBookTitles.size
    })).sort((a, b) => {
      if (a.year !== b.year) return b.year - a.year;
      return b.quarter - a.quarter;
    });
  }, [royaltyReports]);
  
  // Apply filters
  const filteredReports = React.useMemo(() => {
    if (yearFilter === 'all') return quarterlyReports;
    
    return quarterlyReports.filter(report => 
      report.year === parseInt(yearFilter)
    );
  }, [quarterlyReports, yearFilter]);
  
  const handleViewQuarter = (quarter: number, year: number) => {
    // Navigate to the first report of that quarter for now
    const quarterReports = royaltyReports?.filter(r => r.quarter === quarter && r.year === year);
    if (quarterReports && quarterReports.length > 0) {
      navigate(`/author/royalties/${quarterReports[0].id}`);
    }
  };
  
  return (
    <MainLayout allowedRoles={['author']}>
      <PageHeader 
        title="Royalty Reports" 
        description="View all your royalty reports by quarter"
      />
      
      <Card>
        <CardHeader>
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <CardTitle>Your Reports</CardTitle>
            
            <div className="flex flex-wrap gap-2">
              <Select 
                value={yearFilter} 
                onValueChange={setYearFilter}
              >
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="Year" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Years</SelectItem>
                  {years.map(year => (
                    <SelectItem key={year} value={year.toString()}>
                      {year}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        
        <CardContent>
          {isLoading ? (
            <div className="flex justify-center p-8">
              <p>Loading royalty reports...</p>
            </div>
          ) : filteredReports.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Period</TableHead>
                  <TableHead>Books</TableHead>
                  <TableHead className="text-right">Units Sold</TableHead>
                  <TableHead className="text-right">Royalty Amount</TableHead>
                  <TableHead className="text-center">Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredReports.map(report => (
                  <TableRow key={`${report.year}-Q${report.quarter}`}>
                    <TableCell>Q{report.quarter} {report.year}</TableCell>
                    <TableCell className="font-medium">{report.bookCount} book{report.bookCount !== 1 ? 's' : ''}</TableCell>
                    <TableCell className="text-right">{report.totalUnitsSold.toLocaleString()}</TableCell>
                    <TableCell className="text-right">{formatCurrency(report.totalRoyaltyAmount)}</TableCell>
                    <TableCell className="text-center">
                      <Badge 
                        variant={report.status === 'published' ? 'default' : 'secondary'}
                      >
                        {report.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost" 
                        size="sm"
                        onClick={() => handleViewQuarter(report.quarter, report.year)}
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        View
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-12">
              <p className="text-muted-foreground">No royalty reports found</p>
            </div>
          )}
        </CardContent>
      </Card>
    </MainLayout>
  );
};

export default RoyaltyReports;
